import { Droplets, Wrench, Hammer, Gauge, ArrowRight } from "lucide-react";
import { smoothScrollTo } from "../utils/smoothScroll";

const KEGS_IMAGE = "https://images.unsplash.com/photo-1772384480595-f45ee8ab39da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxzdGFpbmxlc3MlMjBzdGVlbCUyMGJlZXIlMjBrZWdzJTIwYnJld2VyeXxlbnwwfHx8fDE3NzU2MTAyMTF8MA&ixlib=rb-4.1.0&q=85";
const LINE_CLEANING_IMAGE = "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/l0f7rlxj_IMG_0820.jpeg";
const MAINTENANCE_IMAGE = "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/eq0p07ob_IMG_0539.png";
const INSTALLATION_IMAGE = "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/8tjd10f0_1073234C-3327-4D91-99BA-322571AAC3A2.png";

const services = [
  {
    icon: Droplets,
    title: "Draft Beer Line Cleaning",
    description: "Routine cleaning ensures every pint is crisp, fresh, and perfectly poured. Eliminate foam, reduce waste, and maximize profit.",
    features: ["Monthly cleaning schedules", "FDA-approved cleaning agents", "Highly skilled technicians", "Detailed service reports"],
    image: LINE_CLEANING_IMAGE,
    size: "large",
  },
  {
    icon: Hammer,
    title: "Tap System Installation",
    description: "Custom-designed draft systems tailored to your space. Professional installation for trouble-free operation from day one.",
    features: ["Custom design consultation", "Quality hardware & glycol systems", "Warranty included"],
    image: INSTALLATION_IMAGE,
    size: "medium",
  },
  {
    icon: Wrench,
    title: "Equipment Maintenance",
    description: "Keep your system running smoothly with scheduled maintenance. Prevent costly breakdowns before they happen.",
    features: ["Preventive maintenance plans", "Parts replacement", "System optimization"],
    image: MAINTENANCE_IMAGE,
    size: "medium",
  },
  {
    icon: Gauge,
    title: "Emergency Repairs",
    description: "Draft system down? We provide expedited turn around times to get your taps flowing again. Fast response, stocked trucks, skilled technicians.",
    features: ["Same-day response", "Fully stocked service vehicles"],
    image: "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/tpmqqoe7_19975EF2-34EF-49DB-AAC5-8A6E30401FB1.png",
    size: "small",
  },
];

export default function ServicesSection() {
  const scrollToContact = () => {
    smoothScrollTo("#contact");
  };

  return (
    <section id="services" className="py-24 bg-[#0A0A0A]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          className="text-center mb-16"
        >
          <p className="overline mb-4" data-testid="services-overline">Our Services</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-4" data-testid="services-heading">
            Complete Draft Beer Solutions
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto text-lg">
            From installation to maintenance, we handle every aspect of your draft system 
            so you can focus on what matters—your customers.
          </p>
        </div>

        {/* Bento Grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6"
        >
          {/* Large Card - Line Cleaning */}
          <div
            className="lg:col-span-8 service-card rounded-2xl overflow-hidden card-hover group"
            data-testid="service-card-cleaning"
          >
            <div className="relative h-64 lg:h-80">
              <img
                loading="lazy"
                src={services[0].image}
                alt={services[0].title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />
            </div>
            <div className="p-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <Droplets className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-xl text-white">{services[0].title}</h3>
              </div>
              <p className="text-[#A1A1AA] mb-6">{services[0].description}</p>
              <ul className="space-y-2 mb-6">
                {services[0].features.map((feature) => (
                  <li key={feature} className="flex items-center text-sm text-[#A1A1AA]">
                    <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mr-3" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={scrollToContact}
                className="inline-flex items-center text-[#D4AF37] hover:text-[#F5D061] font-medium group/btn"
              >
                Schedule Cleaning
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
              </button>
            </div>
          </div>

          {/* Medium Card - Installation */}
          <div
            className="lg:col-span-4 service-card rounded-2xl overflow-hidden card-hover group"
            data-testid="service-card-installation"
          >
            <div className="relative h-48">
              <img
                loading="lazy"
                src={services[1].image}
                alt={services[1].title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <Hammer className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-lg text-white">{services[1].title}</h3>
              </div>
              <p className="text-[#A1A1AA] text-sm mb-4">{services[1].description}</p>
              <button
                onClick={scrollToContact}
                className="inline-flex items-center text-[#D4AF37] hover:text-[#F5D061] text-sm font-medium group/btn"
              >
                Get Quote
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
              </button>
            </div>
          </div>

          {/* Medium Card - Maintenance */}
          <div
            className="lg:col-span-4 service-card rounded-2xl overflow-hidden card-hover group"
            data-testid="service-card-maintenance"
          >
            <div className="relative h-48">
              <img
                loading="lazy"
                src={services[2].image}
                alt={services[2].title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-lg text-white">{services[2].title}</h3>
              </div>
              <p className="text-[#A1A1AA] text-sm mb-4">{services[2].description}</p>
              <button
                onClick={scrollToContact}
                className="inline-flex items-center text-[#D4AF37] hover:text-[#F5D061] text-sm font-medium group/btn"
              >
                Learn More
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
              </button>
            </div>
          </div>

          {/* Small Card - Emergency */}
          <div
            className="lg:col-span-8 service-card rounded-2xl overflow-hidden card-hover"
            data-testid="service-card-emergency"
          >
            <div className="flex flex-col md:flex-row">
              <div className="md:w-2/5 h-48 md:h-auto">
                <img
                  loading="lazy"
                  src={services[3].image}
                  alt={services[3].title}
                  className="w-full h-full object-cover"
                  data-testid="emergency-image"
                />
              </div>
              <div className="flex-1 p-6 flex flex-col justify-center">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <Gauge className="w-5 h-5 text-red-500" />
                  </div>
                  <h3 className="font-heading text-lg text-white">{services[3].title}</h3>
                </div>
                <p className="text-[#A1A1AA] text-sm mb-4">{services[3].description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {services[3].features.map((feature) => (
                    <span
                      key={feature}
                      className="px-3 py-1 bg-[#1A1A1A] border border-[#27272A] rounded-full text-xs text-[#A1A1AA]"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                <a
                  href="tel:603-548-9932"
                  className="btn-gold inline-flex items-center justify-center whitespace-nowrap px-6 py-2.5 text-sm w-fit"
                  data-testid="emergency-call-button"
                >
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
